import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid } from "@material-ui/data-grid";
import Typography from "@material-ui/core/Typography";
import CheckIcon from "@material-ui/icons/Check";
import { Column, OrganizationLogo } from "../../widgets";
import DeactivateOrgButton from "./DeactivateOrgButton";
import ActivateOrgButton from "./ActivateOrgButton";

const useStyles = makeStyles((theme) => ({
  grid: {
    flex: "1 1 auto",
    margin: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
  },
}));

function makeColumns(showDisabledOrgs, makeActionButton) {
  const makeCheckMark = (params) => {
    return <React.Fragment>{params.value && <CheckIcon />}</React.Fragment>;
  };

  let columns = [
    {
      field: "logo",
      headerName: "Logo",
      width: 90,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <OrganizationLogo organizationId={params.value} width={48} height={40} />
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 250,
    },
    {
      field: "seats",
      headerName: "Seats",
      width: 250
    },
    {
      field: "address",
      headerName: "Address",
      width: 200,
    }
  ];

  if (showDisabledOrgs) {
    columns.push({
      field: "disabled",
      headerName: "Location",
      renderCell: makeCheckMark,
      width: 175,
    });
  }

  columns.push({
    field: "action",
    headerName: !showDisabledOrgs ? "Deactivate" : " ",
    renderCell: makeActionButton,
    disableColumnMenu: true,
    sortable: false,
    width: 150,
  });

  return columns;
}

function mapAddressString(address) {
  let addressString = "";
  if (address) {
    if (address.city && address.city != "") {
      addressString += address.city;
    }
    if (address.stateProvince && address.stateProvince != "") {
      addressString += (addressString != "" ? ", " : "") + address.stateProvince;
    }
  }
  return addressString;
}

function mapSnapshotToRows(snapshot) {
  if (!snapshot) return [];

  const rows = snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      logo: doc.id,
      name: data.name,
      disabled: data.disabled ?? false,
      seats: data.subscription?.seats ?? data.seats ?? 1,
      address: data.address ? mapAddressString(data.address) : "",
      action: {
        organizationId: doc.id,
        organizationName: data.name,
        disabled: data.disabled ?? false,
      },
    };
  });

  return rows;
}

function OrganizationListExpanded(props) {
  const { loading, snapshot, error, onItemSelected, onShowSnackbar, showDisabledOrgs } = props;
  const classes = useStyles();

  useEffect(() => {
    document.title = "Organizations - Runyon Data Services";
  });

  const handleSelectionModelChange = (selectionModel) => {
    onItemSelected(selectionModel.length > 0 ? selectionModel[0] : undefined);
  };

  const handleCellClick = (params, event) => {
    if (params.colDef.field == "action") {
      event.stopPropagation();
    }
  };

  const makeActionButton = (params) => {
    const { organizationId, organizationName, disabled } = params.value;

    if (disabled) {
      return (
        <ActivateOrgButton
          organizationId={organizationId}
          onShowSnackbar={onShowSnackbar}
        />
      );
    }

    return (
      <DeactivateOrgButton
        organizationId={organizationId}
        organizationName={organizationName}
        onShowSnackbar={onShowSnackbar}
      />
    );
  };

  const columns = makeColumns(showDisabledOrgs, makeActionButton);
  const rows = mapSnapshotToRows(snapshot);

  return (
    <React.Fragment>
      {rows.length == 0 && !loading && (
        <Column spacing={2} horizontalArrangement="centerHorizontally">
          <img src="images/NoDocuments@2x.png" />
          <Typography>There are no organizations to display</Typography>
        </Column>
      )}
      {(loading || rows.length > 0) && (
        <DataGrid
          className={classes.grid}
          rows={rows}
          columns={columns}
          loading={loading}
          error={error}
          onSelectionModelChange={handleSelectionModelChange}
          onCellClick={handleCellClick}
        />
      )}
    </React.Fragment>
  );
}

OrganizationListExpanded.propTypes = {
  loading: PropTypes.bool,
  snapshot: PropTypes.object,
  error: PropTypes.object,
  onItemSelected: PropTypes.func,
  onShowSnackbar: PropTypes.func,
  showDisabledOrgs: PropTypes.bool,
};

export default OrganizationListExpanded;
